import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  IconButton, Tooltip, Pagination, Autocomplete, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ApiService from '../../functions/apiService';
import { toast } from 'react-toastify';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import moment from 'moment';

const TYPES = ['ACCRUED', 'CARRYOVER', 'PERIOD_START', 'SOLD', 'USED'];

const emptyForm = {
  ET_Emp_Codes: [],
  ET_Emp_Code: '',
  ET_Date: '',
  ET_Type: '',
  ET_Subtype: '',
  ET_Total: '',
  ET_Note: '',
};

const empLabel = (emp) =>
  emp ? `${emp.fullName || `${emp.First_name || ''} ${emp.Last_name || ''}`.trim()} ${emp.Badge_Number ? `(${emp.Badge_Number})` : ''}`.trim() : '';

const AccrualManagement = () => {
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // filters
  const [filterEmps, setFilterEmps] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSubtype, setFilterSubtype] = useState('');

  // sort
  const [sortBy, setSortBy] = useState('ET_Date');
  const [sortDir, setSortDir] = useState('desc');

  // form data
  const [employees, setEmployees] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [formDataLoaded, setFormDataLoaded] = useState(false);

  // add/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // bulk edit
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEdits, setBulkEdits] = useState({});
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const loadFormData = async () => {
    if (formDataLoaded) return;
    try {
      const res = await ApiService.get('/accrual-management/form-data');
      const d = res?.data?.data || res?.data || {};
      setEmployees(d.employees || []);
      setSubTypes(d.sub_types || []);
      setFormDataLoaded(true);
    } catch {
      toast.error('Failed to load form data.');
    }
  };

  const doFetch = async ({ pg, sb, sd, emps, date, type, subtype }) => {
    setLoading(true);
    try {
      const body = {
        page: pg,
        per_page: 25,
        sort_by: sb,
        sort_dir: sd,
      };
      if (emps.length) body.emp_codes = emps.map((e) => e.Emp_Code);
      if (date) { body.start_date = date; body.end_date = date; }
      if (type) body.type = type;
      if (subtype) body.subtype = subtype;

      const res = await ApiService.post('/accrual-management/list', body);
      const d = res?.data || {};
      const fetched = d.data || [];
      setRecords(fetched);
      setMeta({
        current_page: d.current_page || 1,
        last_page: d.last_page || 1,
        total: d.total || 0,
      });
      return fetched;
    } catch {
      toast.error('Failed to load records.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const currentParams = (overrides = {}) => ({
    pg:      overrides.pg      ?? page,
    sb:      overrides.sb      ?? sortBy,
    sd:      overrides.sd      ?? sortDir,
    emps:    overrides.emps    ?? filterEmps,
    date:    overrides.date    ?? filterDate,
    type:    overrides.type    ?? filterType,
    subtype: overrides.subtype ?? filterSubtype,
  });

  useEffect(() => {
    doFetch(currentParams());
  }, []);

  const applyFilters = () => {
    setPage(1);
    doFetch(currentParams({ pg: 1 }));
  };

  const handleSort = (field) => {
    const newDir = sortBy === field && sortDir === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortDir(newDir);
    setPage(1);
    doFetch(currentParams({ sb: field, sd: newDir, pg: 1 }));
  };

  const handlePageChange = (_, p) => {
    setPage(p);
    doFetch(currentParams({ pg: p }));
  };

  const openCreate = async () => {
    await loadFormData();
    setForm(emptyForm);
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    await loadFormData();
    setForm({
      ET_Emp_Code: row.ET_Emp_Code,
      ET_Date: row.ET_Date,
      ET_Type: row.ET_Type,
      ET_Subtype: row.ET_Subtype,
      ET_Total: row.ET_Total,
      ET_Note: row.ET_Note || '',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    const empValid = editMode ? !!form.ET_Emp_Code : form.ET_Emp_Codes.length > 0;
    if (!empValid || !form.ET_Date || !form.ET_Type || !form.ET_Subtype || form.ET_Total === '') {
      toast.error('Please fill all required fields.');
      return;
    }
    setSaving(true);
    try {
      if (editMode) {
        await ApiService.post('/accrual-management/update', form);
        toast.success('Record updated.');
      } else {
        const payload = {
          ET_Emp_Code: form.ET_Emp_Codes.map((e) => e.Emp_Code),
          ET_Date: form.ET_Date,
          ET_Type: form.ET_Type,
          ET_Subtype: form.ET_Subtype,
          ET_Total: form.ET_Total,
          ET_Note: form.ET_Note,
        };
        const res = await ApiService.post('/accrual-management/store', payload);
        toast.success(res?.data?.message || 'Record(s) created.');
      }
      setDialogOpen(false);
      doFetch(currentParams());
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await ApiService.post('/accrual-management/delete', {
        ET_Emp_Code: deleteTarget.ET_Emp_Code,
        ET_Date: deleteTarget.ET_Date,
        ET_Type: deleteTarget.ET_Type,
        ET_Subtype: deleteTarget.ET_Subtype,
      });
      toast.success('Record deleted.');
      setDeleteTarget(null);
      doFetch(currentParams());
    } catch {
      toast.error('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const rowLabel = (row) =>
    row.employee?.fullName ||
    `${row.employee?.First_name || ''} ${row.employee?.Last_name || ''}`.trim() ||
    row.ET_Emp_Code;

  const rowKey = (row) => `${row.ET_Emp_Code}|${row.ET_Date}|${row.ET_Type}|${row.ET_Subtype}`;

  const handleBulkEdit = async () => {
    if (!filterEmps.length) {
      toast.error('Please select 1 employee to enable this feature.');
      return;
    }
    setPage(1);
    const fetched = await doFetch(currentParams({ pg: 1 }));
    const initial = {};
    fetched.forEach((row) => { initial[rowKey(row)] = String(row.ET_Total); });
    setBulkEdits(initial);
    setBulkMode(true);
  };

  const handleBulkCancel = () => {
    setBulkMode(false);
    setBulkEdits({});
  };

  const changedRows = records.filter((row) => {
    const key = rowKey(row);
    return bulkEdits[key] !== undefined && Number(bulkEdits[key]) !== Number(row.ET_Total);
  });

  const handleBulkSubmit = () => {
    if (!changedRows.length) {
      toast.info('No changes to save.');
      return;
    }
    setBulkConfirmOpen(true);
  };

  const handleBulkConfirm = async () => {
    const updates = changedRows.map((row) => ({
      ET_Emp_Code: row.ET_Emp_Code,
      ET_Date: row.ET_Date,
      ET_Type: row.ET_Type,
      ET_Subtype: row.ET_Subtype,
      ET_Total: Number(bulkEdits[rowKey(row)]),
    }));
    setBulkSaving(true);
    try {
      await ApiService.post('/accrual-management/bulk-update', { updates });
      toast.success(`${updates.length} record${updates.length !== 1 ? 's' : ''} updated.`);
      setBulkConfirmOpen(false);
      setBulkMode(false);
      setBulkEdits({});
      doFetch(currentParams());
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Bulk update failed.');
    } finally {
      setBulkSaving(false);
    }
  };

  const SortCell = ({ field, label }) => (
    <TableCell sortDirection={sortBy === field ? sortDir : false}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        <strong>{label}</strong>
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Accrual Management</Typography>
        <Box display="flex" gap={1}>
          {bulkMode ? (
            <>
              <Button variant="outlined" size="small" style={{ height: 38 }} onClick={handleBulkCancel}>
                Cancel
              </Button>
              <ButtonTheme variant="filled" color="blue" style={{ height: 38 }} onClick={handleBulkSubmit}>
                Save Changes
              </ButtonTheme>
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" style={{ height: 38 }} onClick={handleBulkEdit}>
                Bulk Edit
              </Button>
              <ButtonTheme variant="filled" color="blue" style={{ height: 38 }} onClick={openCreate}>
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                Add Accrual
              </ButtonTheme>
            </>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2.5,
          p: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
        }}
      >
        <Autocomplete
          multiple
          size="small"
          options={formDataLoaded ? employees : []}
          getOptionLabel={empLabel}
          value={filterEmps}
          onChange={(_, val) => setFilterEmps(val)}
          onOpen={loadFormData}
          disableCloseOnSelect
          renderTags={(val, getTagProps) =>
            val.map((opt, i) => (
              <Chip
                key={opt.Emp_Code}
                label={opt.fullName || `${opt.First_name || ''} ${opt.Last_name || ''}`.trim()}
                size="small"
                {...getTagProps({ index: i })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Employee" placeholder={filterEmps.length ? '' : 'All employees'} />
          )}
          sx={{ minWidth: 240, flex: '1 1 240px', maxWidth: 360 }}
        />

        <TextField
          size="small"
          label="Date"
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />

        <TextField
          select
          size="small"
          label="Type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value=""><em>All Types</em></MenuItem>
          {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>

        <TextField
          select
          size="small"
          label="Sub Type"
          value={filterSubtype}
          onChange={(e) => setFilterSubtype(e.target.value)}
          SelectProps={{ onOpen: loadFormData }}
          sx={{ width: 160 }}
        >
          <MenuItem value=""><em>All Sub Types</em></MenuItem>
          {subTypes.map((st) => (
            <MenuItem key={st.SO_Value} value={st.SO_Value}>{st.SO_Desc}</MenuItem>
          ))}
        </TextField>

        <Box display="flex" gap={1} ml="auto">
          <Button
            size="small"
            variant="outlined"
            sx={{ height: 38, minWidth: 70 }}
            onClick={() => {
              setFilterEmps([]);
              setFilterDate('');
              setFilterType('');
              setFilterSubtype('');
              setPage(1);
              doFetch({ pg: 1, sb: sortBy, sd: sortDir, emps: [], date: '', type: '', subtype: '' });
            }}
          >
            Clear
          </Button>
          <ButtonTheme variant="filled" color="blue" style={{ height: 38, minWidth: 80 }} onClick={applyFilters}>
            Search
          </ButtonTheme>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortCell field="ET_Emp_Code" label="Employee" />
                  <SortCell field="ET_Date" label="Date" />
                  <SortCell field="ET_Type" label="Type" />
                  <SortCell field="ET_Subtype" label="Sub Type" />
                  <SortCell field="ET_Total" label="Total" />
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{rowLabel(row)}</TableCell>
                      <TableCell>{moment(row.ET_Date).format('MM/DD/YYYY')}</TableCell>
                      <TableCell>{row.ET_Type}</TableCell>
                      <TableCell>{row.ET_Subtype}</TableCell>
                      <TableCell align="right">
                        {bulkMode ? (
                          <TextField
                            size="small"
                            type="number"
                            value={bulkEdits[rowKey(row)] ?? row.ET_Total}
                            onChange={(e) => setBulkEdits((prev) => ({ ...prev, [rowKey(row)]: e.target.value }))}
                            sx={{ width: 90 }}
                            inputProps={{ style: { textAlign: 'right' } }}
                          />
                        ) : row.ET_Total}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.ET_Note || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {!bulkMode && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              {meta.total} record{meta.total !== 1 ? 's' : ''}
            </Typography>
            {meta.last_page > 1 && (
              <Pagination
                count={meta.last_page}
                page={meta.current_page}
                onChange={handlePageChange}
                color="primary"
              />
            )}
          </Box>
        </>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit Accrual Record' : 'Add Accrual Record'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {editMode ? (
              <TextField
                select
                label="Employee *"
                value={form.ET_Emp_Code}
                size="small"
                fullWidth
                disabled
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.Emp_Code} value={emp.Emp_Code}>
                    {empLabel(emp)}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Autocomplete
                multiple
                options={formDataLoaded ? employees : []}
                getOptionLabel={(emp) => empLabel(emp)}
                isOptionEqualToValue={(o, v) => o.Emp_Code === v.Emp_Code}
                value={form.ET_Emp_Codes}
                onChange={(_, val) => setForm((prev) => ({ ...prev, ET_Emp_Codes: val }))}
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((emp, index) => (
                    <Chip key={emp.Emp_Code} label={empLabel(emp)} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Employee *" placeholder={form.ET_Emp_Codes.length ? '' : 'Select employees'} />
                )}
              />
            )}

            <TextField
              label="Date *"
              type="date"
              value={form.ET_Date}
              onChange={handleFormChange('ET_Date')}
              size="small"
              fullWidth
              disabled={editMode}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              label="Type *"
              value={form.ET_Type}
              onChange={handleFormChange('ET_Type')}
              size="small"
              fullWidth
              disabled={editMode}
            >
              {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>

            <TextField
              select
              label="Sub Type *"
              value={form.ET_Subtype}
              onChange={handleFormChange('ET_Subtype')}
              size="small"
              fullWidth
              disabled={editMode}
            >
              {subTypes.map((st) => (
                <MenuItem key={st.SO_Value} value={st.SO_Value}>{st.SO_Desc}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Total *"
              type="number"
              value={form.ET_Total}
              onChange={handleFormChange('ET_Total')}
              size="small"
              fullWidth
            />

            <TextField
              label="Note"
              value={form.ET_Note}
              onChange={handleFormChange('ET_Note')}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <ButtonTheme variant="filled" color="blue" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : editMode ? 'Update' : 'Save'}
          </ButtonTheme>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Confirm Dialog */}
      <Dialog open={bulkConfirmOpen} onClose={() => !bulkSaving && setBulkConfirmOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Confirm Bulk Update</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following {changedRows.length} record{changedRows.length !== 1 ? 's' : ''} will be updated:
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Sub Type</strong></TableCell>
                  <TableCell align="right"><strong>Old Total</strong></TableCell>
                  <TableCell align="right"><strong>New Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changedRows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{rowLabel(row)}</TableCell>
                    <TableCell>{moment(row.ET_Date).format('MM/DD/YYYY')}</TableCell>
                    <TableCell>{row.ET_Type}</TableCell>
                    <TableCell>{row.ET_Subtype}</TableCell>
                    <TableCell align="right">{row.ET_Total}</TableCell>
                    <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {bulkEdits[rowKey(row)]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)} disabled={bulkSaving}>Cancel</Button>
          <ButtonTheme variant="filled" color="blue" onClick={handleBulkConfirm} disabled={bulkSaving}>
            {bulkSaving ? <CircularProgress size={18} /> : 'Confirm'}
          </ButtonTheme>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Accrual Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this record for{' '}
            <strong>{deleteTarget ? rowLabel(deleteTarget) : ''}</strong> on{' '}
            <strong>{deleteTarget ? moment(deleteTarget.ET_Date).format('MM/DD/YYYY') : ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AccrualManagement;
